import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Button, Typography, LinearProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import GameScreenShell from "@components/Home/Games/GameScreenShell";
import SessionSummary, { SessionSummaryStats } from "@components/Home/Games/SessionSummary";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import {
  useStartSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
  useGetContentQuery,
} from "@store/gameApi";
import { RootState } from "@store/store";
import {
  getLevelParams,
  resolveRoundContent,
  computeRoundMetrics,
  type CategoryTapRoundContent,
  type StimulusStep,
} from "./categoryTapContent";
import { getContentBankLabel, getContentBankCategoryLabel } from "@utils/contentBankTranslations";
import {
  gameContinueButtonSx,
  gamePrimaryButtonSx,
  gameSecondaryButtonSx,
} from "@components/Home/Games/gameUi";
import { useGameTimingMode } from "@hooks/useGameTimingMode";
import { useInGameMoreTimeAllowed } from "@hooks/useInGameMoreTimeAllowed";
import {
  adjustCategoryTapTiming,
  getInGameMoreTimeBonusMs,
} from "@utils/gameTiming";
import {
  buildImageCandidateUrls,
  preloadImageUrls,
} from "@utils/gameImage";

export interface CategoryTapGuidedProps {
  guidedSessionId?: string;
  guidedCurrentGame?: GameSessionGame;
  guidedGameIndex?: number;
  guidedTotalGames?: number;
  onGuidedGameComplete?: () => void;
}

const ROUNDS_PER_SESSION = 3;
const SESSION_START_DEDUPE_MS = 2000;
const CATEGORY_SWITCH_PAUSE_MS = 1000;

type Phase = "idle" | "playing" | "switchPause" | "feedback";

interface FreePlayLocationState {
  freePlaySessionId?: string;
  freePlayGames?: GameSessionGame[];
}

interface ItemResponse {
  tapped: boolean;
  missed?: boolean;
}

const CategoryTap: FC<CategoryTapGuidedProps> = ({
  guidedSessionId,
  guidedCurrentGame,
  guidedGameIndex = 0,
  guidedTotalGames = 1,
  onGuidedGameComplete,
}) => {
  const location = useLocation();
  const freePlayState = location.state as FreePlayLocationState | null;
  const freePlaySessionId = freePlayState?.freePlaySessionId;
  const freePlayGame = freePlayState?.freePlayGames?.[0];

  const isGuided = Boolean(guidedSessionId && guidedCurrentGame);
  const isFreePlayFromLibrary = Boolean(freePlaySessionId && freePlayGame);
  const effectiveSessionId = guidedSessionId ?? freePlaySessionId ?? null;
  const effectiveGame = guidedCurrentGame ?? freePlayGame;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId
  );
  const sessionStartedRef = useRef(false);
  const lastSessionStartAtRef = useRef(0);
  const [startSession] = useStartSessionMutation();
  const [sessionStartError, setSessionStartError] = useState<string | null>(null);
  const [submitRound] = useSubmitRoundMutation();
  const [completeSession] = useCompleteSessionMutation();

  const contentSeed = effectiveGame?.contentSeed ?? "";
  const difficulty = effectiveGame
    ? Math.max(1, Math.min(10, effectiveGame.difficulty))
    : 1;
  const gameTimingMode = useGameTimingMode();
  const inGameMoreTimeAllowed = useInGameMoreTimeAllowed();
  const levelParams = useMemo(
    () => adjustCategoryTapTiming(getLevelParams(difficulty), gameTimingMode),
    [difficulty, gameTimingMode]
  );

  const contentSeedForFetch =
    contentSeed ||
    (effectiveSessionId
      ? `${effectiveSessionId}-${GAME_IDS.categoryTap}`
      : "");
  const { data: contentData, error: contentError } = useGetContentQuery(
    { gameId: GAME_IDS.categoryTap, seed: contentSeedForFetch },
    { skip: !contentSeedForFetch }
  );

  const [sessionId, setSessionId] = useState<string | null>(effectiveSessionId);
  const [phase, setPhase] = useState<Phase>("idle");
  const [roundCount, setRoundCount] = useState(0);
  const [roundContent, setRoundContent] = useState<CategoryTapRoundContent | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [responses, setResponses] = useState<ItemResponse[]>([]);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [xpEarned, setXpEarned] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [roundResults, setRoundResults] = useState<{ accuracy: number; completionMs: number }[]>([]);
  const [domainUpdatesSum, setDomainUpdatesSum] = useState<Record<string, number>>({});
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(null);
  const [muted, setMuted] = useState(false);
  const [stepBonusMs, setStepBonusMs] = useState(0);
  const [moreTimeUsedForStep, setMoreTimeUsedForStep] = useState(false);
  const [imageAttemptById, setImageAttemptById] = useState<Record<string, number>>(
    {}
  );
  const submittedForRoundRef = useRef(false);
  const sessionSeedRef = useRef<string | null>(null);
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const switchPauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sessionSeed = useMemo(() => {
    if (sessionSeedRef.current) return sessionSeedRef.current;
    sessionSeedRef.current = `session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    return sessionSeedRef.current;
  }, []);

  const currentStep: StimulusStep | null =
    roundContent ? (roundContent.sequence[currentStepIndex] ?? null) : null;
  const showSwitchPause =
    phase === "switchPause" && roundContent && roundContent.sequence[currentStepIndex];

  const startNewRound = useCallback(
    (roundIndex?: number) => {
      submittedForRoundRef.current = false;
      const idx = roundIndex ?? roundCount;
      const content = resolveRoundContent(
        contentData,
        contentError,
        contentSeed,
        idx,
        levelParams,
        sessionSeed
      );
      setRoundContent(content);
      setImageAttemptById({});
      setCurrentStepIndex(0);
      setResponses([]);
      setRoundStartTime(Date.now());
      setXpEarned(null);
      if (content) {
        setPhase("playing");
      }
    },
    [contentSeed, roundCount, levelParams, contentData, contentError, sessionSeed]
  );

  useEffect(() => {
    if (!roundContent) return;
    const urls = roundContent.sequence.flatMap((step) =>
      buildImageCandidateUrls(step.item.imageUrl)
    );
    const controller = new AbortController();
    preloadImageUrls(urls, { concurrency: 4, signal: controller.signal }).catch(
      () => {}
    );
    return () => controller.abort();
  }, [roundContent]);

  const advanceToNext = useCallback(
    (tapped: boolean, missed = false) => {
      if (!roundContent) return;
      const step = roundContent.sequence[currentStepIndex];
      if (!step) return;

      setResponses((prev) => [...prev, { tapped, missed }]);
      const nextIndex = currentStepIndex + 1;

      if (nextIndex >= roundContent.sequence.length) {
        setPhase("feedback");
        if (!submittedForRoundRef.current) {
          submittedForRoundRef.current = true;
          const allResponses = [...responses, { tapped, missed }];
          const { accuracy, switchScore, missedItems } = computeRoundMetrics(
            roundContent.sequence,
            allResponses
          );
          const completionTimeMs = Date.now() - roundStartTime;
          const totalErrors =
            roundContent.sequence.length -
            Math.round(accuracy * roundContent.sequence.length) +
            missedItems;
          setRoundResults((prev) => [
            ...prev,
            { accuracy, completionMs: completionTimeMs },
          ]);

          if (sessionId) {
            submitRound({
              sessionId,
              body: {
                gameId: GAME_IDS.categoryTap,
                roundNumber: roundCount + 1,
                difficulty,
                accuracy,
                reactionTimeMs: null,
                completionTimeMs,
                errorCount: Math.max(0, totalErrors),
                rawData: {
                  switchScore,
                  missedItems,
                  itemResponses: allResponses,
                },
              },
            })
              .unwrap()
              .then((res) => {
                if (res?.xpEarned != null) {
                  setXpEarned(res.xpEarned);
                  setSessionScore((s) => s + res.xpEarned);
                }
                if (res?.domainUpdates && Object.keys(res.domainUpdates).length > 0) {
                  setDomainUpdatesSum((prev) => {
                    const next = { ...prev };
                    for (const [k, v] of Object.entries(res.domainUpdates)) {
                      next[k] = (next[k] ?? 0) + v;
                    }
                    return next;
                  });
                }
              })
              .catch(() => {});
          } else {
            const xp = accuracy >= 0.8 ? 20 : 10;
            setXpEarned(xp);
            setSessionScore((s) => s + xp);
          }
        }
        return;
      }

      const nextStep = roundContent.sequence[nextIndex];
      const categorySwitched =
        nextStep && nextStep.phaseIndex !== step.phaseIndex;

      if (categorySwitched) {
        setCurrentStepIndex(nextIndex);
        setPhase("switchPause");
        if (switchPauseTimerRef.current) clearTimeout(switchPauseTimerRef.current);
        switchPauseTimerRef.current = setTimeout(() => {
          switchPauseTimerRef.current = null;
          setPhase("playing");
        }, CATEGORY_SWITCH_PAUSE_MS);
        // Optional ding on category switch (respects muted)
        if (typeof window !== "undefined" && window.AudioContext && !muted) {
          try {
            const ctx = new window.AudioContext();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = 880;
            osc.type = "sine";
            gain.gain.setValueAtTime(0.15, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.15);
          } catch {
            // ignore
          }
        }
      } else {
        setCurrentStepIndex(nextIndex);
      }
    },
    [
      roundContent,
      currentStepIndex,
      responses,
      roundStartTime,
      sessionId,
      roundCount,
      difficulty,
      submitRound,
      muted,
    ]
  );

  const handleTap = useCallback(() => {
    if (phase !== "playing" || !currentStep) return;
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    advanceToNext(true);
  }, [phase, currentStep, advanceToNext]);

  const handleSkip = useCallback(() => {
    if (phase !== "playing" || !currentStep) return;
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    advanceToNext(false);
  }, [phase, currentStep, advanceToNext]);

  useEffect(() => {
    setStepBonusMs(0);
    setMoreTimeUsedForStep(false);
  }, [currentStepIndex, roundCount]);

  const stepStartRef = useRef(0);

  useEffect(() => {
    if (phase !== "playing" || !roundContent || !currentStep) return;
    const ms = roundContent.levelParams.timePerItemMs;
    if (ms <= 0) return;
    if (stepBonusMs === 0) {
      stepStartRef.current = Date.now();
    }
    const remaining = Math.max(
      0,
      stepStartRef.current + ms + stepBonusMs - Date.now()
    );
    autoAdvanceTimerRef.current = setTimeout(() => {
      autoAdvanceTimerRef.current = null;
      advanceToNext(false, true);
    }, remaining);
    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
    };
  }, [
    phase,
    roundContent,
    currentStepIndex,
    currentStep,
    advanceToNext,
    stepBonusMs,
  ]);

  useEffect(() => {
    return () => {
      if (switchPauseTimerRef.current) clearTimeout(switchPauseTimerRef.current);
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isGuided || isFreePlayFromLibrary) return;
    if (!selectedPatientId) {
      setSessionStartError(t("games.errors.onlyPatientCanPlay"));
      return;
    }
    if (sessionStartedRef.current) return;
    const now = Date.now();
    if (now - lastSessionStartAtRef.current < SESSION_START_DEDUPE_MS) return;
    lastSessionStartAtRef.current = now;
    sessionStartedRef.current = true;
    setSessionStartError(null);
    let mounted = true;
    startSession({ patientId: selectedPatientId })
      .unwrap()
      .then((res) => {
        if (mounted && res?.sessionId) {
          setSessionId(res.sessionId);
          setSessionStartTime(Date.now());
        }
      })
      .catch((err: { status?: number }) => {
        if (mounted && err?.status === 403) {
          setSessionStartError(t("games.errors.onlyPatientCanPlay"));
        }
      });
    return () => {
      mounted = false;
    };
  }, [isGuided, isFreePlayFromLibrary, selectedPatientId, startSession, t]);

  useEffect(() => {
    if ((isGuided && guidedSessionId) || isFreePlayFromLibrary) {
      setSessionStartTime(Date.now());
    }
  }, [isGuided, guidedSessionId, isFreePlayFromLibrary]);

  useEffect(() => {
    if (sessionStartTime == null || paused) return;
    const id = setInterval(() => {
      setTimerSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, paused]);

  const handleNextRound = useCallback(() => {
    const nextRound = roundCount + 1;
    if (nextRound >= ROUNDS_PER_SESSION) {
      if (isGuided && onGuidedGameComplete) {
        onGuidedGameComplete();
        return;
      }
      const allResults = [...roundResults];
      const totalRounds = allResults.length;
      const avgAccuracy =
        totalRounds > 0
          ? (allResults.reduce((s, r) => s + r.accuracy, 0) / totalRounds) * 100
          : 0;
      const avgCompletionSec =
        totalRounds > 0
          ? allResults.reduce((s, r) => s + r.completionMs, 0) / totalRounds / 1000
          : 0;

      if (sessionId) {
        completeSession({ sessionId })
          .unwrap()
          .then((res) => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgCompletionSec,
              xpEarned: res?.totalXp ?? sessionScore,
              domainChanges: domainUpdatesSum,
            });
          })
          .catch(() => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgCompletionSec,
              xpEarned: sessionScore,
              domainChanges: domainUpdatesSum,
            });
          });
      } else {
        setSummaryStats({
          accuracyPercent: avgAccuracy,
          avgReactionSeconds: avgCompletionSec,
          xpEarned: sessionScore,
          domainChanges: domainUpdatesSum,
        });
      }
      setSessionId(null);
      setShowSessionSummary(true);
      return;
    }
    setRoundCount(nextRound);
    startNewRound(nextRound);
  }, [
    roundCount,
    roundResults,
    sessionId,
    sessionScore,
    domainUpdatesSum,
    isGuided,
    onGuidedGameComplete,
    completeSession,
    startNewRound,
  ]);

  const handleExit = useCallback(() => {
    if (sessionId) completeSession({ sessionId }).catch(() => {});
    navigate(-1);
  }, [sessionId, completeSession, navigate]);

  const handleSummaryContinue = useCallback(() => navigate(-1), [navigate]);

  const handleRequestMoreTime = useCallback(() => {
    if (
      moreTimeUsedForStep ||
      phase !== "playing" ||
      !roundContent ||
      roundContent.levelParams.timePerItemMs <= 0
    )
      return;
    const bonus = getInGameMoreTimeBonusMs(GAME_IDS.categoryTap);
    if (bonus <= 0) return;
    setStepBonusMs((ms) => ms + bonus);
    setMoreTimeUsedForStep(true);
  }, [moreTimeUsedForStep, phase, roundContent]);

  const progress =
    phase === "idle"
      ? 0
      : (roundCount + (phase === "feedback" ? 1 : 0)) / ROUNDS_PER_SESSION;

  const feedbackText =
    phase === "feedback" ? t("games.categoryTap.feedbackGood") : null;

  if (showSessionSummary && summaryStats) {
    return (
      <SessionSummary stats={summaryStats} onContinue={handleSummaryContinue} />
    );
  }

  return (
    <GameScreenShell
      gameName={t("games.categoryTap.title")}
      level={difficulty}
      progress={progress}
      feedback={feedbackText}
      timerSeconds={sessionStartTime != null ? timerSeconds : null}
      score={sessionScore > 0 ? sessionScore : null}
      paused={paused}
      onBack={handleExit}
      onPause={() => setPaused((p) => !p)}
      gameIndex={isGuided ? guidedGameIndex : undefined}
      totalGames={isGuided ? guidedTotalGames : undefined}
      muted={muted}
      onMuteChange={setMuted}
      moreTime={
        inGameMoreTimeAllowed &&
        phase === "playing" &&
        (roundContent?.levelParams.timePerItemMs ?? 0) > 0
          ? {
              onRequest: handleRequestMoreTime,
              disabled: moreTimeUsedForStep,
            }
          : undefined
      }
    >
      {phase === "idle" && (
        <Box
          textAlign="center"
          py={4}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {sessionStartError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {sessionStartError}
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            {t("games.categoryTap.instructionsTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("games.categoryTap.instructions")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => startNewRound()}
            sx={gameContinueButtonSx}
          >
            {t("games.categoryTap.startRound")}
          </Button>
        </Box>
      )}

      {(phase === "playing" || phase === "switchPause" || phase === "feedback") &&
        roundContent && (
        <>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: "1.5rem", mb: 1.5, textAlign: "center" }}
          >
            {phase === "feedback" ? (
              t("games.categoryTap.feedbackGood")
            ) : phase === "switchPause" && showSwitchPause ? (
              <Trans
                i18nKey="games.categoryTap.newCategory"
                values={{
                  category: getContentBankCategoryLabel(
                    t,
                    showSwitchPause.category,
                    showSwitchPause.category
                  ),
                }}
                components={{
                  highlight: (
                    <Box
                      component="span"
                      sx={{
                        color: "primary.main",
                        fontWeight: 800,
                        textUnderlineOffset: 3,
                      }}
                    />
                  ),
                }}
              />
            ) : currentStep ? (
              <Trans
                i18nKey="games.categoryTap.categoryPrompt"
                values={{
                  category: getContentBankCategoryLabel(
                    t,
                    currentStep.category,
                    currentStep.category
                  ),
                }}
                components={{
                  highlight: (
                    <Box
                      component="span"
                      sx={{
                        color: "primary.main",
                        fontWeight: 800,
                        textUnderlineOffset: 3,
                      }}
                    />
                  ),
                }}
              />
            ) : null}
          </Typography>

          {phase === "playing" && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={
                  roundContent.sequence.length > 0
                    ? (currentStepIndex / roundContent.sequence.length) * 100
                    : 0
                }
                color="primary"
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {t("games.categoryTap.itemProgress", {
                  current: currentStepIndex + 1,
                  total: roundContent.sequence.length,
                })}
              </Typography>
            </Box>
          )}

          {phase === "playing" && currentStep && (
            <Box
              sx={{
                flex: 1,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 2.5, sm: 3 },
              }}
            >
              {(() => {
                const imageCandidates = buildImageCandidateUrls(
                  currentStep.item.imageUrl
                );
                const imageAttempt = imageAttemptById[currentStep.item.id] ?? 0;
                const imageSrc =
                  imageCandidates[
                    Math.min(imageAttempt, Math.max(0, imageCandidates.length - 1))
                  ];
                return (
                  <>
              <Box
                sx={{
                  width: { xs: "min(86vw, 340px)", sm: "min(62vw, 280px)", md: 280 },
                  minWidth: { xs: "min(86vw, 340px)", sm: "min(62vw, 280px)", md: 280 },
                  minHeight: { xs: "min(86vw, 340px)", sm: "min(62vw, 280px)", md: 280 },
                  borderRadius: 2,
                  border: "3px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  pt: { xs: 1, sm: 1 },
                  gap: { xs: 0.5, sm: 0.75 },
                  mx: "auto",
                  mb: 0,
                }}
              >
                <Box
                  sx={{
                    width: { xs: "min(80vw, 320px)", sm: "min(56vw, 250px)", md: 250 },
                    height: { xs: "min(80vw, 320px)", sm: "min(56vw, 250px)", md: 250 },
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {roundContent.levelParams.showImage && imageSrc ? (
                    <Box
                      component="img"
                      src={imageSrc}
                      alt=""
                      onError={() =>
                        setImageAttemptById((prev) => ({
                          ...prev,
                          [currentStep.item.id]: Math.min(
                            (prev[currentStep.item.id] ?? 0) + 1,
                            Math.max(0, imageCandidates.length - 1)
                          ),
                        }))
                      }
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Typography variant="h6" color="text.secondary">
                      {getContentBankLabel(
                        t,
                        currentStep.item.id,
                        currentStep.item.label
                      )
                        .slice(0, 1)
                        .toUpperCase()}
                    </Typography>
                  )}
                </Box>
                {roundContent.levelParams.showLabels && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0,
                      fontSize: { xs: "0.98rem", sm: "0.95rem" },
                      fontWeight: 600,
                      textAlign: "center",
                      lineHeight: 1.15,
                    }}
                    color="text.secondary"
                  >
                    {getContentBankLabel(
                      t,
                      currentStep.item.id,
                      currentStep.item.label
                    )}
                  </Typography>
                )}
              </Box>
                  </>
                );
              })()}

              <Box
                display="flex"
                gap={2}
                justifyContent="center"
                flexWrap="wrap"
                sx={{ mt: 0.5, mb: 0, alignSelf: "center" }}
              >
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  onClick={handleTap}
                  sx={gamePrimaryButtonSx}
                >
                  {t("games.categoryTap.tapButton")}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleSkip}
                  sx={gameSecondaryButtonSx}
                >
                  {t("games.categoryTap.skipButton")}
                </Button>
              </Box>

              {roundContent.levelParams.timePerItemMs > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0 }}
                >
                  {t("games.categoryTap.timePerItem", {
                    seconds: roundContent.levelParams.timePerItemMs / 1000,
                  })}
                </Typography>
              )}
            </Box>
          )}

          {phase === "switchPause" && (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              {t("games.categoryTap.getReady")}
            </Typography>
          )}

          {phase === "feedback" && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
              mt={2}
              mb={2}
            >
              {xpEarned != null && (
                <Typography variant="body2" color="primary.main">
                  +{xpEarned} XP
                </Typography>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={handleNextRound}
                sx={gameContinueButtonSx}
              >
                {roundCount + 1 >= ROUNDS_PER_SESSION
                  ? t("games.categoryTap.finishSession")
                  : t("games.categoryTap.nextRound")}
              </Button>
            </Box>
          )}
        </>
      )}
    </GameScreenShell>
  );
};

export default CategoryTap;
